import { create } from "zustand"

/** Who the answer is written for. The API says "revenue"; the product says "the department". */
export type Side = "taxpayer" | "revenue"

/** Where the State came from: the chip ("chosen"), the notice's GSTIN, the question, the last choice, or nothing (All India). */
export type StateSource = "chosen" | "notice" | "question" | "last" | "none"

export interface ThreadContext {
  side: Side
  state_code: string
  /** The State's name, as the server resolved it. */
  state?: string
  state_source?: StateSource
}

/** "00": no State known; no High Court is the reader's own and only the Supreme Court binds. */
export const ALL_INDIA = "00"

/** GST State codes and names (backend/nira/nira/engine/states.py). */
export const STATES: ReadonlyArray<readonly [code: string, name: string]> = [
  ["00", "All India"],
  ["37", "Andhra Pradesh"], ["35", "Andaman and Nicobar Islands"], ["12", "Arunachal Pradesh"],
  ["18", "Assam"], ["10", "Bihar"], ["04", "Chandigarh"], ["22", "Chhattisgarh"],
  ["26", "Dadra and Nagar Haveli and Daman and Diu"], ["07", "Delhi"], ["30", "Goa"], ["24", "Gujarat"],
  ["06", "Haryana"], ["02", "Himachal Pradesh"], ["01", "Jammu and Kashmir"], ["20", "Jharkhand"],
  ["29", "Karnataka"], ["32", "Kerala"], ["38", "Ladakh"], ["31", "Lakshadweep"],
  ["23", "Madhya Pradesh"], ["27", "Maharashtra"], ["14", "Manipur"], ["17", "Meghalaya"],
  ["15", "Mizoram"], ["13", "Nagaland"], ["21", "Odisha"], ["34", "Puducherry"], ["03", "Punjab"],
  ["08", "Rajasthan"], ["11", "Sikkim"], ["33", "Tamil Nadu"], ["36", "Telangana"], ["16", "Tripura"],
  ["09", "Uttar Pradesh"], ["05", "Uttarakhand"], ["19", "West Bengal"],
]

const NAMES = new Map(STATES)
export const stateName = (code: string | undefined | null) => (code && NAMES.get(code)) || ""

export const DEFAULT_CONTEXT: ThreadContext = { side: "taxpayer", state_code: ALL_INDIA, state_source: "none" }
const KEY = "nira-context"

function load(): ThreadContext {
  try {
    const v = JSON.parse(window.localStorage.getItem(KEY) || "null")
    if (v && (v.side === "taxpayer" || v.side === "revenue") && NAMES.has(v.state_code)) {
      return { side: v.side, state_code: v.state_code, state_source: v.state_code === ALL_INDIA ? "none" : "last" }
    }
  } catch {
    /* storage blocked or malformed: use the default */
  }
  return DEFAULT_CONTEXT
}

/** "for a taxpayer" / "for the department". */
export const sideWords = (side: Side) => (side === "revenue" ? "for the department" : "for a taxpayer")

interface ReaderContextStore {
  /** What a new question starts with: the reader's last choice on this device. */
  preferred: ThreadContext
  /** Each open thread's context, as the server holds it. */
  threads: Record<string, ThreadContext>
  prefer: (ctx: ThreadContext) => void
  setThread: (id: string, ctx: ThreadContext) => void
}

export const useReaderContext = create<ReaderContextStore>((set) => ({
  preferred: typeof window === "undefined" ? DEFAULT_CONTEXT : load(),
  threads: {},
  prefer: (ctx) => {
    try {
      window.localStorage.setItem(KEY, JSON.stringify({ side: ctx.side, state_code: ctx.state_code }))
    } catch {
      /* storage blocked: the choice holds for this visit */
    }
    set({ preferred: { side: ctx.side, state_code: ctx.state_code, state_source: ctx.state_code === ALL_INDIA ? "none" : "last" } })
  },
  setThread: (id, ctx) => set((s) => ({ threads: { ...s.threads, [id]: ctx } })),
}))

/** Read a thread's context out of an API payload; null when the server sent none. */
export function contextFrom(v: unknown): ThreadContext | null {
  const c = v && typeof v === "object" ? (v as Record<string, unknown>).context : null
  if (!c || typeof c !== "object") return null
  const o = c as Record<string, unknown>
  const side = o.side === "revenue" ? "revenue" : o.side === "taxpayer" ? "taxpayer" : null
  const code = typeof o.state_code === "string" ? o.state_code : null
  if (!side || !code) return null
  const source = o.state_source
  const known: StateSource[] = ["chosen", "notice", "question", "last", "none"]
  return {
    side,
    state_code: code,
    state: typeof o.state === "string" ? o.state : stateName(code),
    state_source: known.includes(source as StateSource) ? (source as StateSource) : undefined,
  }
}

/** Why the chip shows this State, in a few words; empty when the reader chose it. */
export function sourceWords(ctx: ThreadContext): string {
  switch (ctx.state_source) {
    case "notice": return "Read from the GSTIN on your notice."
    case "question": return "Read from your question."
    case "last": return "Your last choice."
    case "none": return "No State given, so no High Court binds you; only the Supreme Court does."
    default: return ""
  }
}

/** The fields of `next` that differ from `prev`, for a PATCH; null when nothing changed. */
export function changedContext(prev: ThreadContext, next: ThreadContext): Partial<Pick<ThreadContext, "side" | "state_code">> | null {
  const out: Partial<Pick<ThreadContext, "side" | "state_code">> = {}
  if (next.side !== prev.side) out.side = next.side
  if (next.state_code !== prev.state_code) out.state_code = next.state_code
  return Object.keys(out).length ? out : null
}
